import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  signInAnonymously,
  signInWithPopup,
  signInWithCredential,
  signOut,
  GoogleAuthProvider,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import {
  getFunctions,
  connectFunctionsEmulator,
} from 'firebase/functions';

// Public web SDK config. apiKey here is a public identifier, not a secret.
const firebaseConfig = {
  apiKey: 'AIzaSyDZbnDJHdfEtg8VCmH9KkpOauCwSGOmPi8',
  authDomain: 'spirits-game.firebaseapp.com',
  projectId: 'spirits-game',
  storageBucket: 'spirits-game.firebasestorage.app',
  messagingSenderId: '366188217117',
  appId: '1:366188217117:web:8f0b13e17d71f6e6d7d7f4',
  measurementId: 'G-XYVYGT5NJQ',
};

export const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
// Agent runs in europe-north1, co-located with Firestore.
export const functions = getFunctions(app, 'europe-north1');

// Connect to emulators on localhost — single switch, no env var needed.
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

let emulatorsConnected = false;
if (isLocalhost && !emulatorsConnected) {
  try {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    emulatorsConnected = true;
    // eslint-disable-next-line no-console
    console.info('[spirits] connected to firebase emulators');
  } catch (e) {
    // Already connected (HMR), or emulators not running — fall through to prod.
    // eslint-disable-next-line no-console
    console.warn('[spirits] emulator connection skipped', e);
  }
}

// Resolve with the first auth state Firebase reports — i.e. once any persisted
// session (anonymous OR admin) has been restored from storage.
function firstAuthState(): Promise<User | null> {
  return new Promise<User | null>((resolve, reject) => {
    const unsub = onAuthStateChanged(
      auth,
      (u) => {
        unsub();
        resolve(u);
      },
      (err) => {
        unsub();
        reject(err);
      },
    );
  });
}

export async function ensureAnonymousAuth(): Promise<User> {
  // Wait for persistence to restore BEFORE deciding to sign in: on a fresh page
  // load auth.currentUser is still null for a beat, and signing in anonymously
  // here would clobber a just-restored session (e.g. the /admin login).
  const restored = await firstAuthState();
  if (restored) return restored;
  await signInAnonymously(auth);
  const u = await firstAuthState();
  if (!u) throw new Error('anonymous sign-in did not produce a user');
  return u;
}

// Admin sign-in. Used by the /admin page to upgrade from the anonymous session to
// a Google identity whose email claim gates template writes (see firestore.rules).
// Replaces the current (anonymous) user; on the emulator the Google widget lets you
// type any email, so sign in as e.g. dev@sunbisoft.com locally.
const googleProvider = new GoogleAuthProvider();

export async function signInWithGoogle(): Promise<User> {
  const cred = await signInWithPopup(auth, googleProvider);
  return cred.user;
}

// Drop the admin (Google) session and return to a fresh anonymous one so the rest
// of the app keeps working after leaving /admin.
export async function signOutToAnonymous(): Promise<User> {
  await signOut(auth);
  await signInAnonymously(auth);
  return auth.currentUser!;
}

// Whether the app is talking to the local emulators (see isLocalhost above).
export const IS_LOCALHOST = isLocalhost;

// LOCALHOST-ONLY backdoor admin login — for manual + headless browser testing.
// The Auth emulator accepts a JSON string in place of a Google ID token and mints
// a real google.com identity carrying exactly those claims, so this signs in as a
// VERIFIED dev@sunbisoft.com without any OAuth popup. It cannot work in prod: real
// Firebase rejects the fake token, AND this function hard-refuses off localhost.
// Also exposed as `window.__spiritsDevAdminLogin()` for headless triggering.
export const DEV_ADMIN_EMAIL = 'dev@sunbisoft.com';

export async function signInAsLocalAdmin(): Promise<User> {
  if (!isLocalhost) throw new Error('dev admin login is localhost-only');
  const claims = JSON.stringify({
    sub: 'dev-admin',
    email: DEV_ADMIN_EMAIL,
    email_verified: true,
    name: 'Dev Admin',
  });
  const result = await signInWithCredential(auth, GoogleAuthProvider.credential(claims));
  return result.user;
}

if (isLocalhost && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__spiritsDevAdminLogin = signInAsLocalAdmin;
}
